'use client';

import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import Decimal from 'decimal.js';

import { createApiClient } from 'lib/api/createApiClient';
import { useAuthStore } from 'lib/stores/authStore';
import { isUserManagedSubaccount, useSubAccountStore } from 'stores/zustand/subAccountStore';
import config from 'utils/config';

const api = createApiClient({
    baseURL: `${config.exchangeApiUrl}/v1`,
});

/**
 * One row per (subaccount, exchange shard) the user has on the exchange. A
 * user with only the primary subaccount can still receive multiple rows when
 * that subaccount holds balances on multiple shards.
 *
 * Only the fields the account UI reads are typed strongly — keep numeric
 * values as strings to preserve precision (see `FixedPointDollars` convention).
 */
export interface UserBalanceEntry {
    subaccount_number: number;
    exchange_shard: number;
    balance: string;
    available_balance?: string;
}

export interface SubaccountShardBalances {
    exchange_shard: number;
    balances: UserBalanceEntry[];
}

export interface PrimaryExchangeBalance {
    exchange_shard: number;
    balance: string;
}

interface GetBalancesResponse {
    subaccount_balances: UserBalanceEntry[];
}

export const userBalancesKeys = {
    all: ['userBalances'] as const,
    user: (userId: string) => [...userBalancesKeys.all, userId] as const,
};

const USER_BALANCES_POLL_MS = 60_000;

export function hasMultipleSubaccounts(balances: UserBalanceEntry[]): boolean {
    return balances.some((entry) => entry.subaccount_number > 0);
}

/**
 * Expands user-managed subaccounts across every advertised exchange shard.
 * Query Exchange stores balances sparsely, so an absent pair is a zero balance
 * rather than a reason to hide that exchange from the subaccount breakdown.
 * Managed/ISV subaccounts remain sparse because the user does not own their
 * provisioning lifecycle.
 */
export function getVisibleSubaccountBalances(
    balances: UserBalanceEntry[],
    advertisedExchangeShards: number[],
): UserBalanceEntry[] {
    const balancesByPair = new Map<string, UserBalanceEntry>();

    for (const balance of balances) {
        balancesByPair.set(`${balance.subaccount_number}:${balance.exchange_shard}`, balance);
    }

    const userManagedSubaccounts = new Set(
        balances
            .filter((balance) => isUserManagedSubaccount(balance.subaccount_number))
            .map((balance) => balance.subaccount_number),
    );
    const exchangeShards = new Set(advertisedExchangeShards);

    for (const subaccountNumber of userManagedSubaccounts) {
        for (const exchangeShard of exchangeShards) {
            const key = `${subaccountNumber}:${exchangeShard}`;
            if (balancesByPair.has(key)) continue;
            balancesByPair.set(key, {
                subaccount_number: subaccountNumber,
                exchange_shard: exchangeShard,
                balance: '0',
            });
        }
    }

    return Array.from(balancesByPair.values()).sort(
        (left, right) => left.subaccount_number - right.subaccount_number || left.exchange_shard - right.exchange_shard,
    );
}

/** One exact cash balance per exchange index for the primary subaccount only. */
export function getPrimaryExchangeBalances(balances: UserBalanceEntry[]): PrimaryExchangeBalance[] {
    const byShard = new Map<number, Decimal>();

    for (const entry of balances) {
        if (entry.subaccount_number !== 0) continue;
        byShard.set(entry.exchange_shard, (byShard.get(entry.exchange_shard) ?? new Decimal(0)).plus(entry.balance));
    }

    return Array.from(byShard.entries())
        .sort(([left], [right]) => left - right)
        .map(([exchangeShard, balance]) => ({
            exchange_shard: exchangeShard,
            balance: balance.toString(),
        }));
}

/** Groups real user-managed subaccount balances by exchange shard. */
export function getSubaccountShardBalances(balances: UserBalanceEntry[]): SubaccountShardBalances[] {
    const byShard = new Map<number, Map<number, UserBalanceEntry>>();

    for (const entry of balances) {
        if (!isUserManagedSubaccount(entry.subaccount_number)) continue;
        const shardBalances = byShard.get(entry.exchange_shard) ?? new Map<number, UserBalanceEntry>();
        shardBalances.set(entry.subaccount_number, entry);
        byShard.set(entry.exchange_shard, shardBalances);
    }

    return Array.from(byShard.entries())
        .sort(([left], [right]) => left - right)
        .map(([exchangeShard, shardBalances]) => ({
            exchange_shard: exchangeShard,
            balances: Array.from(shardBalances.values()).sort(
                (left, right) => left.subaccount_number - right.subaccount_number,
            ),
        }));
}

async function fetchUserBalances(userId: string, signal?: AbortSignal): Promise<UserBalanceEntry[]> {
    const response = await api.get<GetBalancesResponse>(`/users/${userId}/balances`, { signal });
    return response.data.subaccount_balances ?? [];
}

/**
 * Slow-polls `/users/{id}/balances` and mirrors returned data (including a
 * fresh cached response) into `useSubAccountStore.subaccountEnabled`. The API
 * returns one row per (subaccount, shard), so the capability is derived from
 * the presence of a non-primary subaccount rather than the number of balance
 * rows. Mount once at the app level (e.g. at the authenticated layout) so the
 * flag is current everywhere. Pass enabled: false in consumers that only need
 * to observe this shared cache without adding requests or polling timers.
 */
export function useUserBalances({ enabled = true }: { enabled?: boolean } = {}) {
    const userId = useAuthStore((s) => s.userId);
    const isMember = useAuthStore((s) => s.isLoggedInMember);

    const query = useQuery({
        queryKey: userBalancesKeys.user(userId ?? ''),
        queryFn: ({ signal }) => fetchUserBalances(userId!, signal),
        refetchInterval: USER_BALANCES_POLL_MS,
        staleTime: USER_BALANCES_POLL_MS,
        enabled: enabled && !!userId && isMember,
    });

    // Mirror query data rather than updating the store only inside queryFn.
    // React Query can reuse a fresh cached response when the same account logs
    // back in, in which case queryFn does not run and the logout-reset flag
    // would otherwise remain false until a refetch or page reload.
    useEffect(() => {
        if (!query.data) return;
        useSubAccountStore.getState().actions.subaccountEnabledUpdated(hasMultipleSubaccounts(query.data));
    }, [query.data]);

    return query;
}

export function sumBalancesInDollars(rows: UserBalanceEntry[]): number {
    return rows.reduce((sum, row) => sum.plus(row.balance), new Decimal(0)).toNumber();
}

/**
 * Total balance summed across every subaccount row, in dollars. Matches the
 * account-wide sum svc-auth checks against its $10 residual limit on
 * deactivation, unlike the per-subaccount `useCashInDollars`.
 */
export function useTotalBalanceInDollars(): number {
    const userId = useAuthStore((s) => s.userId);
    const isMember = useAuthStore((s) => s.isLoggedInMember);

    const { data } = useQuery({
        queryKey: userBalancesKeys.user(userId ?? ''),
        queryFn: ({ signal }) => fetchUserBalances(userId!, signal),
        enabled: !!userId && isMember,
        select: sumBalancesInDollars,
    });

    return data ?? 0;
}
